import React, { Suspense, lazy } from 'react';

import CheckboxField from './formElements/CheckboxField';
import ColorPickerField from './formElements/ColorPickerField';
import DateField from './formElements/DateField';
import DateTimeField from './formElements/DateTimeField';
import DecimalField from './formElements/DecimalField';
import DropdownField from './formElements/DropdownField';
import ErrorField from './formElements/ErrorField';
import MultiSelectField from './formElements/MultiSelectField';
import PasswordField from './formElements/PasswordField';
import TextAreaField from './formElements/TextAreaField';
import TextField from './formElements/TextField';

// Lazy-loaded: TinyMCE is large (JS + skin CSS) and should only be
// pulled into the bundle for pages that actually render a richtext field.
const RichTextEditor = lazy(() => import('./formElements/RichTextEditor'));

interface FormFieldProps {
  id: string;
  label: string;
  value?: string | boolean | number | Date | string[];
  onChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => void;
  placeholder?: string;
  type?:
    | 'text'
    | 'textarea'
    | 'email'
    | 'password'
    | 'number'
    | 'decimal'
    | 'dropdown'
    | 'checkbox'
    | 'date'
    | 'datetime'
    | 'datetime-local'
    | 'richtext'
    | 'multiselect'
    | 'colorpicker';
  options?: {
    value: string | undefined;
    label: string | React.ReactNode;
    description?: string;
    searchText?: string;
  }[];
  error?: string;
  disabled?: boolean;
  maxLength?: number;
  helper?: string | React.ReactNode;
  subtext?: string | React.ReactNode;
  required?: boolean;
  onClick?: () => void;
  rows?: number;
  isLoading?: boolean;
  searchable?: boolean;
  onSearch?: (query: string) => void;
  searchDebounceMs?: number;
  allowFutureDates?: boolean;
  showTodayLink?: boolean;
  textCase?:
    | 'sentencecase'
    | 'titlecase'
    | 'uppercase'
    | 'lowercase'
    | 'standard';
  min?: number;
  max?: number;
  step?: number;
  decimalPlaces?: number;
}

const FormField: React.FC<FormFieldProps> = ({
  id,
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  options,
  error,
  disabled = false,
  maxLength,
  helper,
  subtext,
  required,
  onClick,
  rows = 4,
  isLoading,
  searchable,
  onSearch,
  searchDebounceMs,
  allowFutureDates,
  showTodayLink,
  textCase,
  min,
  max,
  step,
  decimalPlaces,
}) => (
  <div className="mb-6 text-left">
    <label
      htmlFor={id}
      className="mb-2 block text-sm font-semibold text-ink dark:text-white"
    >
      {label}
      {required && <span className="ml-1 text-red-600">*</span>}
    </label>
    {type === 'dropdown' && options && (
      <DropdownField
        id={id}
        value={value as string}
        onChange={onChange}
        options={options}
        disabled={disabled}
        placeholder={placeholder}
        isLoading={isLoading}
        searchable={searchable}
        onSearch={onSearch}
        searchDebounceMs={searchDebounceMs}
      />
    )}
    {['date', 'datetime'].includes(type) && (
      <DateField
        id={id}
        value={value as string}
        onChange={onChange}
        disabled={disabled}
        allowFutureDates={allowFutureDates}
        showTodayLink={showTodayLink}
      />
    )}
    {type === 'datetime-local' && (
      <DateTimeField
        id={id}
        value={value as string}
        onChange={onChange}
        disabled={disabled}
        allowFutureDates={allowFutureDates}
      />
    )}
    {type === 'checkbox' && (
      <CheckboxField
        id={id}
        value={value as boolean}
        onChange={onChange}
        disabled={disabled}
        subtext={subtext}
      />
    )}
    {['text', 'email', 'number'].includes(type) && (
      <TextField
        id={id}
        type={type}
        value={value as string}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        onClick={onClick}
        textCase={textCase}
      />
    )}
    {type === 'decimal' && (
      <DecimalField
        id={id}
        value={(value ?? '') as string | number}
        onChange={onChange as (e: React.ChangeEvent<HTMLInputElement>) => void}
        placeholder={placeholder}
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        decimalPlaces={decimalPlaces}
      />
    )}
    {type === 'password' && (
      <PasswordField
        id={id}
        value={value as string}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
      />
    )}
    {type === 'textarea' && (
      <TextAreaField
        id={id}
        value={value as string}
        onChange={
          onChange as (e: React.ChangeEvent<HTMLTextAreaElement>) => void
        }
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        rows={rows}
      />
    )}
    {type === 'richtext' && (
      <Suspense
        fallback={
          <div className="h-[420px] animate-pulse rounded-md border border-gray-300 bg-gray-50 dark:border-white/20 dark:bg-white/5" />
        }
      >
        <RichTextEditor
          id={id}
          value={value as string}
          onChange={(html) => {
            const e = {
              target: {
                id,
                value: html,
                type: 'richtext',
              },
            } as unknown as React.ChangeEvent<HTMLInputElement>;
            onChange(e);
          }}
          disabled={disabled}
          placeholder={placeholder}
        />
      </Suspense>
    )}
    {type === 'multiselect' && options && (
      <MultiSelectField
        id={id}
        value={value as string[]}
        onChange={(values) => {
          const e = {
            target: {
              id,
              value: values,
              type: 'multiselect',
            },
          } as unknown as React.ChangeEvent<HTMLInputElement>;
          onChange(e);
        }}
        options={options.map((opt) => ({
          value: opt.value,
          label: opt.label,
          description: opt.description,
          searchText:
            opt.searchText || (typeof opt.label === 'string' ? opt.label : ''),
        }))}
        disabled={disabled}
        placeholder={placeholder}
      />
    )}
    {type === 'colorpicker' && (
      <ColorPickerField
        id={id}
        value={value as string}
        onChange={onChange}
        disabled={disabled}
      />
    )}
    {helper && (
      <p className="mt-1 text-xs text-ink-muted dark:text-white/50">{helper}</p>
    )}
    {error && <ErrorField error={error} />}
  </div>
);

export default FormField;
